/**
 * FormField — label / control / hint / inline-error wrapper (§14.3).
 * Wires ids and aria automatically: inputs rendered inside pick up
 * aria-describedby (hint + error) and aria-invalid via context.
 */

import { createContext, useContext, useId, useMemo, type ReactNode } from "react";
import { CircleAlert } from "lucide-react";
import { cx } from "../utils/cx";

export interface FormFieldContextValue {
  readonly controlId: string;
  readonly describedBy: string | undefined;
  readonly invalid: boolean;
}

const FormFieldContext = createContext<FormFieldContextValue | null>(null);

/** Consumed by TextInput/Textarea/Select; usable by custom controls too. */
export function useFormField(): FormFieldContextValue | null {
  return useContext(FormFieldContext);
}

export interface FormFieldProps {
  readonly label: ReactNode;
  /** Marks the label with a required indicator (announced via the input's own `required`). */
  readonly required?: boolean;
  readonly hint?: ReactNode;
  /** Inline validation slot — string or richer node. Presence = invalid. */
  readonly error?: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
}

export function FormField({ label, required = false, hint, error, children, className }: FormFieldProps) {
  const id = useId();
  const controlId = `${id}-control`;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const invalid = error != null && error !== false && error !== "";

  const describedBy =
    [hint ? hintId : null, invalid ? errorId : null].filter(Boolean).join(" ") || undefined;

  const ctx = useMemo<FormFieldContextValue>(
    () => ({ controlId, describedBy, invalid }),
    [controlId, describedBy, invalid],
  );

  return (
    <div className={cx("ui-field", className)}>
      <label className="ui-field-label" htmlFor={controlId}>
        {label}
        {required ? (
          <span className="ui-field-required" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      <FormFieldContext.Provider value={ctx}>{children}</FormFieldContext.Provider>
      {hint ? (
        <p className="ui-field-hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      {/* Always-mounted live region so new errors are announced inline. */}
      <div aria-live="polite">
        {invalid ? (
          <p className="ui-field-error" id={errorId}>
            <CircleAlert size={14} aria-hidden="true" />
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}
